import { createContext, ReactNode, useMemo } from 'react';
import { useQuerySubscription } from 'react-datocms';
import { buildTheme } from '@carbonify/ui';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import usePreview from 'hooks/usePreview';
import { queryOptions } from 'services/graphql/client';
import { Locale } from 'services/graphql/types';

import queryBrandConfig, { BrandConfigResult } from './queryBrandConfig';

type AlternateLinks = { href: string; locale: Locale; label: string }[];
type BrandConfigWithPrebuiltTheme = Omit<BrandConfigResult, 'theme'> & {
  theme?: string | undefined;
  alternateLinks?: AlternateLinks;
  categories?: string[];
};

export const ConfigContext = createContext<BrandConfigWithPrebuiltTheme>(
  null as unknown as BrandConfigWithPrebuiltTheme,
);

interface Props {
  config: BrandConfigWithPrebuiltTheme;
  alternateLinks: AlternateLinks;
  categories?: string[];
  children: ReactNode;
}

export default function ConfigProvider({ config, alternateLinks, categories, children }: Props) {
  const { isPreview, tenant, brandId, locale, drafts = true, token } = usePreview();
  const { data, error } = useQuerySubscription({
    query: queryBrandConfig,
    variables: { filter: brandId ? { id: { eq: brandId } } : { tenant: { eq: tenant } }, locale },
    enabled: isPreview && (!!token as false),
    ...queryOptions,
    token,
    includeDrafts: drafts,
  });

  const providedConfig = useMemo(() => {
    if (!isPreview) return { ...config, alternateLinks, categories };
    if (!data) return undefined;

    return {
      ...data.brand,
      locale,
      alternateLinks,
      browser: data.brand?.theme?.browser || {},
      theme: data.brand?.theme ? buildTheme(data.brand.theme) : undefined,
      categories,
    };
  }, [config, data, isPreview, locale, alternateLinks, categories]);

  if (isPreview && !token) return <LoadingOverlay error="Token not provided" />;
  if (!providedConfig) return isPreview ? <LoadingOverlay error={error?.message} /> : null;
  if (isPreview && !data?.brand) return <LoadingOverlay error={'Brand not found'} />;

  return <ConfigContext.Provider value={providedConfig}>{children}</ConfigContext.Provider>;
}
